import type { Filter, FilterDataSwitch } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import { getIsPinkPeopleEnabled } from '@/app/components/search/pinkpeople/utils.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import { firstCharUpper } from '@/utils/stringUtils.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { FLASH_SALES_ICON } from '@/app/common/icons/promotionIcons';

type CreateDiscountFilters = {
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  loggedIn: boolean;
  commonData: CommonData;
  localization: Localization;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
};
export const createDiscountFilters = ({
  searchParams,
  searchInfoData,
  loggedIn,
  commonData,
  localization,
  dynamicFiltersResponse,
  translationDictionary,
}: CreateDiscountFilters): Filter[] => {
  const { searchMode } = searchInfoData;
  const { lastMinuteDeals } = localization.configValuesApp;

  // PINK PEOPLE
  const showPinkPeople = getIsPinkPeopleEnabled({
    searchInfoData,
    commonData,
    localization,
  });
  const pinkPeopleFilter: Filter | undefined = showPinkPeople
    ? {
        name: FILTER_NAME.PINK_PEOPLE,
        type: FILTER_TYPE.SWITCH,
        data: {
          label: 'Pink People',
          selected: searchParams?.[URLParameters.PINK_PEOPLE] === '1',
          icon: 'pinkpeople',
          tooltip: 'Pink People Deals',
        } as FilterDataSwitch,
      }
    : undefined;

  // PRO DISCOUNTS: HO only by business decision, https://lastminute.atlassian.net/browse/SLIDERS-1898
  const showLoyaltyDeals =
    searchParams?.[URLParameters.FILTER_LOYALTY_DEALS] === '1' ||
    (searchMode === SearchModes.HO && dynamicFiltersResponse?.promotions?.loyalty_deals !== undefined);
  const loyaltyDealsFilter: Filter | undefined = showLoyaltyDeals
    ? {
        name: FILTER_NAME.LOYALTY_DEALS,
        type: FILTER_TYPE.SWITCH,
        disabled: dynamicFiltersResponse?.promotions?.loyalty_deals === 0,
        data: {
          label: getTranslationServer('vacation.oss.filters.loyalty.label', translationDictionary),
          selected: searchParams?.[URLParameters.FILTER_LOYALTY_DEALS] === '1',
          icon: 'color/loyalty_black',
          counter: dynamicFiltersResponse?.promotions?.loyalty_deals,
          tooltip: getTranslationServer('vacation.oss.filters.loyalty.tooltip', translationDictionary),
        } as FilterDataSwitch,
      }
    : undefined;

  // FLASH SALES
  const showFlashSales = dynamicFiltersResponse?.promotions?.flash_sales !== undefined;
  const flashSalesFilter: Filter | undefined = showFlashSales
    ? {
        name: FILTER_NAME.FLASH_SALES,
        type: FILTER_TYPE.SWITCH,
        disabled: dynamicFiltersResponse?.promotions?.flash_sales === 0,
        data: {
          label: lastMinuteDeals
            ? getTranslationServer('vacation.oss.filters.last_minute_deals.title', translationDictionary)
            : getTranslationServer('vacation.oss.filters.flash_sales.title', translationDictionary),
          selected: searchParams?.[URLParameters.FLASH_SALES] === '1',
          icon: lastMinuteDeals ? 'last_minute_deals_rebrand' : FLASH_SALES_ICON,
          counter: dynamicFiltersResponse?.promotions?.flash_sales,
          tooltip: getTranslationServer('vacation.oss.filters.flash_sales.tooltip', translationDictionary),
        } as FilterDataSwitch,
      }
    : undefined;

  // TSH
  const showTSH =
    searchMode === SearchModes.HO &&
    (dynamicFiltersResponse ? dynamicFiltersResponse?.promotions?.tsh !== undefined : true);
  const tshFilter: Filter | undefined = showTSH
    ? {
        name: FILTER_NAME.TSH,
        type: FILTER_TYPE.SWITCH,
        disabled: dynamicFiltersResponse?.promotions?.tsh === 0,
        data: {
          label: getTranslationServer('vacation.oss.filters.top_secret_hotel.title', translationDictionary),
          selected: searchParams?.[URLParameters.TOP_SECRET_HOTEL] === '1',
          icon: 'tsh',
          counter: dynamicFiltersResponse?.promotions?.tsh,
          tooltip: getTranslationServer('vacation.oss.filters.top_secret_hotel.tooltip', translationDictionary),
        } as FilterDataSwitch,
      }
    : undefined;

  // MY PRICE
  const showMyPrice =
    loggedIn &&
    searchMode === SearchModes.HO &&
    (dynamicFiltersResponse ? dynamicFiltersResponse?.promotions?.myprice !== undefined : true);
  const myPriceLabel = getTranslationServer('vacation.oss.cards.myprice_label_html', translationDictionary).replace(
    /<[^>]*>?/gm,
    ' '
  );
  const myPriceFilter: Filter | undefined = showMyPrice
    ? {
        name: FILTER_NAME.MY_PRICE,
        type: FILTER_TYPE.SWITCH,
        disabled: dynamicFiltersResponse?.promotions?.myprice === 0,
        data: {
          label: firstCharUpper(myPriceLabel),
          selected: searchParams?.[URLParameters.MY_PRICE] === '1',
          icon: 'my_price',
          counter: dynamicFiltersResponse?.promotions?.myprice,
          tooltip: getTranslationServer('vacation.oss.filters.myprice.tooltip', translationDictionary),
        } as FilterDataSwitch,
      }
    : undefined;

  return [
    ...[pinkPeopleFilter],
    ...[loyaltyDealsFilter],
    ...[flashSalesFilter],
    ...[tshFilter],
    ...[myPriceFilter],
  ].filter((f) => !!f);
};
